import type { PharmacyMarker } from "./pharmacy-map.types";

const CACHE_PREFIX = "pharmacy-map:";

export interface CachedPostalCodeCenter {
	lat?: number | null;
	lng?: number | null;
	notFound?: boolean;
}

export interface CachedNearbyVendors {
	vendors: PharmacyMarker[];
	actualRadius: number;
}

export function isPharmacyMapRateLimitError(error?: string): boolean {
	return error === "Too Many Requests";
}

function readCache<T>(key: string): T | null {
	if (typeof sessionStorage === "undefined") {
		return null;
	}
	try {
		const raw = sessionStorage.getItem(key);
		if (!raw) {
			return null;
		}
		return JSON.parse(raw) as T;
	} catch {
		return null;
	}
}

function writeCache(key: string, value: unknown): void {
	if (typeof sessionStorage === "undefined") {
		return;
	}
	try {
		sessionStorage.setItem(key, JSON.stringify(value));
	} catch {
		// Ignore quota errors — cache is a best-effort optimization.
	}
}

export function getCachedPostalCodeCenter(
	postalCode: string,
): CachedPostalCodeCenter | null {
	return readCache<CachedPostalCodeCenter>(
		`${CACHE_PREFIX}postal-center:${postalCode}`,
	);
}

export function setCachedPostalCodeCenter(
	postalCode: string,
	value: CachedPostalCodeCenter,
): void {
	writeCache(`${CACHE_PREFIX}postal-center:${postalCode}`, value);
}

/**
 * The radius is one session-wide choice on purpose: it is shared between PLP
 * and PDP and survives a Courier ↔ Pickup switch (MKP-2054), so it is not
 * keyed by postal code or delivery method.
 */
export function getCachedRadius(): string | null {
	return readCache<string>(`${CACHE_PREFIX}radius`);
}

export function setCachedRadius(radius: string): void {
	writeCache(`${CACHE_PREFIX}radius`, radius);
}

export function getCachedNearbyVendors(
	postalCode: string,
): CachedNearbyVendors | null {
	return readCache<CachedNearbyVendors>(
		`${CACHE_PREFIX}nearby-vendors:${postalCode}`,
	);
}

export function setCachedNearbyVendors(
	postalCode: string,
	value: CachedNearbyVendors,
): void {
	writeCache(`${CACHE_PREFIX}nearby-vendors:${postalCode}`, value);
}
